import React, { Fragment, useState, useMemo, useEffect } from "react";
import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";
import uniq from "lodash/uniq";
import ListItemRow, {
  getDefaultListItemRowClasses
} from "components/ListItemRow";
import { RiAddLine, RiCloseLine } from "react-icons/ri";
import Paragraphs from "components/Paragraphs";

const getReducedItems = (list = []) =>
  list.reduce(
    (memo, item) => {
      if (item.highlighted) {
        memo.highlighted.push(item);
      } else {
        memo.notHighlighted.push(item);
      }
      return memo;
    },
    {
      highlighted: [],
      notHighlighted: []
    }
  );

const getExpandedState = (list = [], expanded = false) =>
  list.reduce((memo, { id, expandable }) => {
    if (expandable) {
      memo[id] = false;
    }
    return memo;
  }, {});

const toggleAll = ({
  setExpanded,
  setCollapsed,
  setCondensed,
  condensed,
  expanded,
  collapsed
}) => () => {
  const updatedExpanded = cloneDeep(expanded);
  for (const key in expanded) {
    if (Object.hasOwnProperty.call(updatedExpanded, key)) {
      updatedExpanded[key] = collapsed;
    }
  }
  setExpanded(updatedExpanded);
  setCollapsed(!collapsed);
  setCondensed(!condensed);
};

const getDefaultClasses = () => {
  return {
    container: "w-100 pb0",
    title: {
      container: "flex flex-row pv3",
      copy: "f5 fw6"
    },
    highlightedList: {
      list: "list ma0 pa0"
    },
    notHighlighted: {
      list: "list ma0 pa0",
      more: {
        container: "flex flex-row justify-between pv3",
        title: "f5 fw6",
        action: "f5 fw6 pointer"
      }
    },
    listItemRow: getDefaultListItemRowClasses()
  };
};

const DescriptionList = ({
  title,
  more,
  list,
  returns,
  idPrefix,
  customClasses,
  child,
  condensed: initCondensed,
  expanded: initExpanded,
  triggerFormatter
}) => {
  const classes = merge(getDefaultClasses(), customClasses);
  const initialExpandedState = getExpandedState(list, initExpanded);

  const [expanded, setExpanded] = useState(initialExpandedState);
  const [collapsed, setCollapsed] = useState(true);
  const [condensed, setCondensed] = useState(Boolean(initCondensed));
  const orderedItems = useMemo(() => getReducedItems(list), []);

  let hide = false;
  if (child) {
    hide = condensed;
  }

  const triggerLabel = `${collapsed ? "Expand" : "Collapse"} all`;

  /**
   * If all items are open / closed we need to sync the collapsed state to that
   */
  const expandedStates = uniq(Object.values(expanded));
  const uniqStates = expandedStates.length;
  useEffect(() => {
    if (uniqStates === 1) {
      setCollapsed(!expandedStates[0]);
    }
  }, [expanded]);

  const hasNoItems =
    !orderedItems.highlighted.length && !orderedItems.notHighlighted.length;

  return (
    <div className={classes.container}>
      {title && (
        <div className={classes.title.container}>
          <div className={classes.title.copy}>{title}</div>
        </div>
      )}
      {hasNoItems ? (
        <div className="bt hairline-1 w-100 pt2">
          <div className="lh-copy pt1 f6">
            {title ? `Has no ${title.toLowerCase()}` : "Has none"}
          </div>
        </div>
      ) : (
        <Fragment>
          <ul className={classes.highlightedList.list}>
            {orderedItems.highlighted.map((item, itemIx) => (
              <ListItemRow
                key={itemIx}
                item={item}
                idPrefix={idPrefix}
                expanded={expanded}
                setExpanded={setExpanded}
              />
            ))}
          </ul>
          {orderedItems.notHighlighted.length > 0 && (
            <Fragment>
              <div className={classes.notHighlighted.more.container}>
                {more && more.title && (
                  <div className={classes.notHighlighted.more.title}>
                    {more.title}
                  </div>
                )}
                <div
                  className={`notHighlighted--action flex flex-row items-center ${classes.notHighlighted.more.action}`}
                  onClick={toggleAll({
                    setExpanded,
                    setCollapsed,
                    expanded,
                    condensed,
                    collapsed,
                    setCondensed
                  })}
                >
                  <div className="pr1" style={{ marginBottom: -2 }}>
                    {collapsed ? (
                      <RiAddLine size={14} />
                    ) : (
                      <RiCloseLine size={14} />
                    )}
                  </div>
                  {triggerFormatter
                    ? triggerFormatter(collapsed)
                    : triggerLabel}
                  <style jsx>{`
                    .notHighlighted--action {
                      color: rgba(0, 0, 0, 0.6);
                    }
                    .notHighlighted--action:hover {
                      color: #383838;
                    }
                  `}</style>
                </div>
              </div>
              {!hide && (
                <ul className={classes.notHighlighted.list}>
                  {orderedItems.notHighlighted.map((item, itemIx) => (
                    <ListItemRow
                      key={itemIx}
                      item={item}
                      idPrefix={idPrefix}
                      expanded={expanded}
                      setExpanded={setExpanded}
                      customClasses={classes.listItemRow}
                    />
                  ))}
                </ul>
              )}
            </Fragment>
          )}
        </Fragment>
      )}
      {returns && (
        <div className="pt3">
          <div className={classes.title.container}>
            <div className={classes.title.copy}>Returns</div>
          </div>
          <div className="bt hairline-1 w-100 pt2">
            <Paragraphs paragraphs={returns} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DescriptionList;
